import {
  Box,
  CreditCard,
  Home,
  LogOut,
  Plug,
  Rocket,
  Settings,
  Shield,
} from "lucide-react";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useAppStore } from "../../store/useAppStore";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Logo } from "../ui/logo";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { SettingsDialog } from "./SettingsDialog";

interface SidebarItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: string | number;
  disabled?: boolean;
  /** Opens a modal instead of navigating (Settings). */
  asDialog?: boolean;
}

const sidebarItems: SidebarItem[] = [
  { name: "Projects", icon: Box, href: "projects" },
  { name: "Integrations", icon: Plug, href: "integrations" },
  { name: "Dashboard", icon: Home, href: "dashboard", disabled: true },
  { name: "Deployments", icon: Rocket, href: "deployments", disabled: true },
];

const bottomSidebarItems: SidebarItem[] = [
  { name: "Billing", icon: CreditCard, href: "billing", disabled: true },
  { name: "Settings", icon: Settings, href: "settings", asDialog: true },
];

const visibleItems = sidebarItems.filter((i) => !i.disabled);
const visibleBottomItems = bottomSidebarItems.filter((i) => !i.disabled);

function SidebarButton({
  item,
  isActive,
  collapsed,
  onClick,
}: {
  item: SidebarItem;
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  const labelId = `sidebar-${item.href}`;

  const button = (
    <Button
      variant="ghost"
      className={cn(
        // h-11 (44px), not the Button default h-10: these measured 39x40 and
        // a navigation rail is the last place to make the target small.
        "relative h-11 w-full justify-start",
        collapsed ? "px-2" : "px-3",
        isActive
          ? "bg-selected text-selected-foreground hover:bg-selected hover:text-selected-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
      )}
      onClick={onClick}
      aria-label={collapsed ? item.name : undefined}
      aria-current={isActive ? "page" : undefined}
      aria-describedby={collapsed ? undefined : labelId}
    >
      <Icon
        className={cn("h-4 w-4 shrink-0", collapsed ? "mx-auto" : "mr-3")}
        aria-hidden="true"
      />
      {!collapsed && (
        <>
          <span id={labelId} className="flex-1 text-left">
            {item.name}
          </span>
          {item.badge && (
            <span
              className={cn(
                "ml-auto rounded-full px-1.5 py-0.5 text-2xs font-medium",
                item.badge === "Beta"
                  ? "bg-info-bg text-info-fg"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </Button>
  );

  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">
        {item.name}
        {item.badge ? ` · ${item.badge}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

function getInitials(name?: string, email?: string): string {
  const source = name?.trim() || email?.trim() || "";
  if (!source) return "U";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * The account menu, at the foot of the rail.
 *
 * It used to live in the top bar, which left that bar holding the brand, a
 * sidebar toggle and an avatar — three unrelated things. Navigation and
 * identity belong together in the rail; the bar is now just search.
 */
function AccountMenu({
  collapsed,
  onOpenSettings,
}: {
  collapsed: boolean;
  onOpenSettings: () => void;
}) {
  const { user, logout } = useAppStore();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start",
            collapsed ? "h-11 px-0" : "h-auto px-2 py-2"
          )}
          aria-label="Account menu"
        >
          <Avatar className={cn("h-7 w-7 shrink-0", collapsed && "mx-auto")}>
            <AvatarFallback className="bg-accent text-2xs font-medium text-accent-foreground">
              {getInitials(user?.name, user?.email)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <span className="ml-2 min-w-0 flex-1 text-left">
              <span className="block truncate text-sm font-medium">
                {user?.name || "Account"}
              </span>
              <span className="block truncate text-xs font-normal text-muted-foreground">
                {user?.email}
              </span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56" side="right" align="end">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-0.5">
            <p className="truncate text-sm font-medium leading-none">
              {user?.name}
            </p>
            <p className="truncate text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onOpenSettings}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={logout}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SidebarNav({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppStore();
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const handleItemClick = (item: SidebarItem) => {
    if (item.asDialog) {
      setSettingsOpen(true);
      onNavigate?.();   // close the mobile drawer behind the modal
      return;
    }
    navigate(`/${item.href}`);
    onNavigate?.();
  };

  const currentPath = location.pathname.split("/")[1] || "projects";

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto py-4">
          <nav
            className={cn("space-y-0.5", collapsed ? "px-2.5" : "px-3")}
            aria-label="Main navigation"
          >
            {visibleItems.map((item) => (
              <SidebarButton
                key={item.name}
                item={item}
                isActive={currentPath === item.href}
                collapsed={collapsed}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </nav>
        </div>

        {visibleBottomItems.length > 0 && (
          <div className="border-t border-sidebar-border py-4">
            <nav
              className={cn("space-y-0.5", collapsed ? "px-2.5" : "px-3")}
              aria-label="Account navigation"
            >
              {visibleBottomItems.map((item) => (
                <SidebarButton
                  key={item.name}
                  item={item}
                  isActive={!item.asDialog && currentPath === item.href}
                  collapsed={collapsed}
                  onClick={() => handleItemClick(item)}
                />
              ))}
              {user?.role === "admin" && (
                <SidebarButton
                  item={{ name: "Admin Console", icon: Shield, href: "admin" }}
                  isActive={currentPath === "admin"}
                  collapsed={collapsed}
                  onClick={() => { navigate("/admin"); onNavigate?.(); }}
                />
              )}
            </nav>

            <div className={cn("pt-2", collapsed ? "px-2.5" : "px-3")}>
              <AccountMenu
                collapsed={collapsed}
                onOpenSettings={() => {
                  setSettingsOpen(true);
                  onNavigate?.();
                }}
              />
            </div>
          </div>
        )}
      </div>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </TooltipProvider>
  );
}

export function Sidebar() {
  const { mobileNavOpen, setMobileNavOpen } = useAppStore();

  return (
    <>
      {/* Desktop: a permanent icon rail. With only a handful of destinations
          the labels earn little and cost 240px of page width on every screen;
          each icon keeps its name in a tooltip (and in aria-label). The mobile
          drawer below still shows labels, where there is room for them. */}
      <aside
        className="hidden h-full w-16 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex"
        aria-label="Primary"
      >
        {/* Brand — the rail's head, and the home link. Mark only: the rail is
            64px wide and the full lockup's wordmark cannot fit in it. */}
        <div className="flex h-14 shrink-0 items-center justify-center">
          <Link
            to="/projects"
            aria-label="Avahana — go to projects"
            className="flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* 32px in a 64px rail: large enough to lead the nav icons (20px)
                rather than read as one of them, with 12px clear above and
                below inside the 56px brand row. */}
            <Logo wordmark={false} markSize={32} />
          </Link>
        </div>

        {/* Nav — fills remaining height */}
        <div className="flex flex-1 flex-col overflow-hidden min-h-0">
          <SidebarNav collapsed />
        </div>
      </aside>

      {/* Mobile: off-canvas drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="flex w-64 flex-col bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetHeader className="border-b border-sidebar-border px-4 py-3 text-left">
            <SheetTitle>
              <Logo size="sm" />
            </SheetTitle>
          </SheetHeader>
          <SidebarNav
            collapsed={false}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
